import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Viewport3DRenderMode, ViewportMPRRenderMode } from '@core/types';
import { useLocalforage } from '@hooks';
import { useAppConfig } from '@state';
import { isFusionable, isMammoable, isNuclearMedAvailable } from '@utils';
import { SettingGroupID } from 'types';
import { useDicomPrint, useViewportGrid } from '../../contextProviders';
import Dropdown from '../Dropdown';
import Typography from '../Typography';

enum ViewerPlugin {
	Default = 'default',
	MPR = 'mpr',
	remoteMPR = 'remoteMPR',
	remoteRender3D = 'remoteRender3D',
	clientRender3D = 'clientRender3D',
	fusionPetCT = 'fusionPetCT',
	remoteRenderEndo = 'remoteRenderEndo',
	mammo = 'mammo',
	dicomPrint = 'dicomPrint',
	nuclearMed = 'nuclearMed',
}

export const PluginSwitch = ({
	servicesManager,
	commandsManager,
	onMobile = false,
}: withAppTypes<{
	onMobile?: boolean;
}>) => {
	const [appConfig] = useAppConfig();
	const { t } = useTranslation('Header');
	const type = onMobile ? t('ShortViewerPlugin', { returnObjects: true }) : t('ViewerPlugin', { returnObjects: true });
	const { state } = useDicomPrint();
	// state dùng tạm
	const [currentMenu, setCurrentMenu] = useState<`${ViewerPlugin}`>(ViewerPlugin.Default);

	const { displaySetService, hangingProtocolService } = servicesManager.services;
	const [{ viewports, activeViewportId }] = useViewportGrid();
	const [fusionable, setFusionable] = useState(false);
	const [nmAvailable, setNmAvailable] = useState(false);
	const activeViewport = viewports.get(activeViewportId);
	const activeDisplaySet =
		displaySetService &&
		activeViewport &&
		activeViewport.displaySetInstanceUIDs &&
		activeViewport.displaySetInstanceUIDs &&
		activeViewport.displaySetInstanceUIDs.length &&
		activeViewport.displaySetInstanceUIDs[0]
			? displaySetService.getDisplaySetByUID(activeViewport.displaySetInstanceUIDs[0])
			: undefined;
	const [savedRender3DConfig] = useLocalforage(SettingGroupID.render3D, null);
	const render3DMode = (savedRender3DConfig && savedRender3DConfig.renderMode) || appConfig.default3DRenderSetting.renderMode;

	const [savedRenderMPRConfig] = useLocalforage(SettingGroupID.renderMPR, null);
	// tạm dùng chung default client với 3D
	const renderMPRMode = (savedRenderMPRConfig && savedRenderMPRConfig.renderMode) || appConfig.default3DRenderSetting.renderMode;

	useEffect(() => {
		if (!displaySetService) {
			return;
		}
		/// Thêm delay 0 ms để displaySet được tải cho ca CT/MR. TODO: Tìm giải pháp tốt hơn
		const checkFusionStatus = () => {
			setTimeout(() => {
				const displaySets = displaySetService.getActiveDisplaySets();
				if (displaySets.length > 0) {
					const canFuse = isFusionable(servicesManager);
					const canNM = isNuclearMedAvailable(servicesManager);

					setFusionable(canFuse);
					setNmAvailable(canNM);
				}
			}, 0);
		};

		checkFusionStatus();

		const { unsubscribe } = displaySetService.subscribe(displaySetService.EVENTS.DISPLAY_SETS_ADDED, checkFusionStatus);

		return () => {
			unsubscribe();
		};
	}, [servicesManager, displaySetService]);

	const mammoable = useMemo(() => activeDisplaySet && isMammoable(servicesManager), [activeDisplaySet, servicesManager]);
	const switchToFusion = useCallback(() => {
		commandsManager.run({
			commandName: 'showModalSelectFusionSeries',
		});
	}, [commandsManager]);

	const switchToMammo = useCallback(() => {
		commandsManager.run({
			commandName: 'setHangingProtocol',
			commandOptions: {
				protocolId: 'mammo',
			},
		});
	}, [commandsManager]);

	const switchToEndo = useCallback(() => {
		commandsManager.run({
			commandName: 'setHangingProtocol',
			commandOptions: {
				protocolId: 'remoteRenderEndo',
			},
		});
	}, [commandsManager]);

	const switchToMPR3D = useCallback(() => {
		if (renderMPRMode === ViewportMPRRenderMode.Server) {
			setCurrentMenu(ViewerPlugin.remoteMPR);
			commandsManager.run({
				commandName: 'setHangingProtocol',
				commandOptions: {
					protocolId: 'remoteMPR',
				},
			});
		} else {
			// Mặc định là Client Render (mpr)
			setCurrentMenu(ViewerPlugin.MPR);
			commandsManager.run({
				commandName: 'setHangingProtocol',
				commandOptions: {
					protocolId: 'mpr',
				},
			});
		}
	}, [commandsManager, renderMPRMode]);

	const switchTo2D = useCallback(() => {
		setCurrentMenu(ViewerPlugin.Default);
		commandsManager.run({
			commandName: 'setHangingProtocol',
			commandOptions: {
				protocolId: 'default',
				isHangingProtocolLayout: false,
				initialImageOptions: {
					preset: 'middle',
				},
				// stageIndex: 0
				// reset: true
			},
		});
	}, [commandsManager]);

	const switchTo3D = useCallback(() => {
		if (render3DMode === Viewport3DRenderMode.Server) {
			setCurrentMenu(ViewerPlugin.remoteRender3D);
			commandsManager.run({
				commandName: 'setHangingProtocol',
				commandOptions: {
					protocolId: 'remoteRender3D',
				},
			});
		} else if (render3DMode === Viewport3DRenderMode.Client) {
			setCurrentMenu(ViewerPlugin.clientRender3D);
			commandsManager.run({
				commandName: 'setHangingProtocol',
				commandOptions: {
					protocolId: 'clientRender3D_only3D',
				},
			});
		}
	}, [commandsManager, render3DMode]);

	const switchToNuclearMed = useCallback(() => {
		setCurrentMenu(ViewerPlugin.nuclearMed);
		commandsManager.run({
			commandName: 'setHangingProtocol',
			commandOptions: {
				protocolId: 'nuclearMed',
			},
		});
	}, [commandsManager]);

	const menuOptions = useMemo(
		() => [
			{
				title: type['2d'],
				onClick: switchTo2D,
				id: ViewerPlugin.Default,
				active: currentMenu === ViewerPlugin.Default,
				enabled: true,
			},
			{
				title: type['mpr'],
				onClick: switchToMPR3D,
				// ID linh hoạt dựa trên chế độ render hiện tại hoặc menu đang chọn
				id: currentMenu === ViewerPlugin.remoteMPR ? ViewerPlugin.remoteMPR : ViewerPlugin.MPR,
				// Active nếu currentMenu là một trong hai loại MPR
				active: currentMenu === ViewerPlugin.MPR || currentMenu === ViewerPlugin.remoteMPR,
				enabled: activeDisplaySet?.isReconstructable,
			},
			{
				title: type['3d'],
				onClick: switchTo3D,
				id: currentMenu === ViewerPlugin.remoteRender3D ? ViewerPlugin.remoteRender3D : ViewerPlugin.clientRender3D,
				active: currentMenu === ViewerPlugin.remoteRender3D || currentMenu.includes(ViewerPlugin.clientRender3D),
				enabled: activeDisplaySet?.isReconstructable,
			},
			{
				title: type['fusionPetCT'],
				onClick: switchToFusion,
				id: ViewerPlugin.fusionPetCT,
				active: currentMenu === ViewerPlugin.fusionPetCT,
				enabled: fusionable,
			},
			{
				title: type['endo'],
				onClick: switchToEndo,
				id: ViewerPlugin.remoteRenderEndo,
				active: currentMenu === ViewerPlugin.remoteRenderEndo,
				enabled: activeDisplaySet?.isReconstructable,
			},
			{
				title: type['mammo'],
				onClick: switchToMammo,
				id: ViewerPlugin.mammo,
				active: currentMenu === ViewerPlugin.mammo,
				enabled: mammoable,
			},
			{
				title: type['nuclearMed'],
				onClick: switchToNuclearMed,
				id: ViewerPlugin.nuclearMed,
				active: currentMenu === ViewerPlugin.nuclearMed,
				enabled: nmAvailable
			},
		],
		[
			type, 
			switchTo2D, 
			currentMenu, 
			switchToMPR3D, 
			activeDisplaySet?.isReconstructable, 
			switchTo3D, 
			switchToFusion, 
			fusionable, 
			switchToEndo, 
			mammoable, 
			switchToMammo, 
			switchToNuclearMed,
			nmAvailable
		],
	);

	useEffect(() => {
		const { unsubscribe } = hangingProtocolService.subscribe(hangingProtocolService.EVENTS.PROTOCOL_CHANGED, ({ protocol }) => {
			setCurrentMenu(protocol.id);
		});

		return () => {
			unsubscribe();
		};
	}, [hangingProtocolService]);

	const title = menuOptions.find((item) => item.active)?.title || '';

	return (
		<>
			{state.endablePrintDicomMode ? (
				<div className="items-center w-full text-center">
					<Typography>DICOM PRINT</Typography>
				</div>
			) : (
				<Dropdown
					id="switch-viewers"
					showDropdownIcon={true}
					list={menuOptions}
					alignment="left"
					itemsClassName={'text-white h-7 text-xs'}
					showBorders={false}
				>
					<div className="mx-2 text-sm text-common-bright">{title}</div>
				</Dropdown>
			)}
		</>
	);
};
